import { useApolloClient, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import * as orgRoutes from 'src/app/account/routes';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { URLModal } from 'src/components/common/URLModal';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { accountServicesQuery } from 'src/hooks/useAccountHasSupergraphs';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePlanErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { GeneralSettingsPageQuery } from '../../graphSettingsPage/generalPage/GeneralSettingsPage';
import { DeleteVariantMutation } from '../../graphSettingsPage/variantsPage/SourceVariantsPage';
import { variantSettingsRouteConfig } from '../../routes';

interface Props {
  // Ideally this would be a fragment.
  data: GraphQLTypes.VariantGeneralSettingsPageQuery;
}

export const DeleteVariantSettingsCard = ({ data: graphData }: Props) => {
  const history = useHistory();
  const location = useLocation();
  const graph = graphData?.graph;
  const graphType = graph?.graphType;
  const client = useApolloClient();
  const { graphVariant } = useRouteParams(variantSettingsRouteConfig);
  const [currentAccountId] = useCurrentAccountId();
  const serviceId = graph?.id ?? '';

  const [deleteTag, { loading, error }] = useMutation(DeleteVariantMutation, {
    variables: { serviceId, schemaTag: graphVariant },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: accountServicesQuery,
        variables: { accountId: currentAccountId },
      },
      {
        query: GeneralSettingsPageQuery,
        variables: { graphVariant, serviceId },
        context: appLinkContext({ catchErrors: [ignorePlanErrors] }),
      },
    ],
    onCompleted: () => {
      client.cache.evict({
        id: client.cache.identify({
          __typename: 'Account',
          id: currentAccountId,
        }),
      });
      if (currentAccountId && graphType) {
        history.push(orgRoutes.graphs.path({ orgId: currentAccountId }));
      }
    },
  });

  const derivedVariantCount = graph?.variant?.derivedVariantCount || 0;
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Delete variant</CardTitle>
          <CardDescription>
            Delete this variant <b>({graphVariant})</b> from the{' '}
            <b>{graph?.title}</b> graph.
          </CardDescription>
          <PermissionWarning
            graphId={graph?.id}
            graphPermissions="canDelete"
            childRenderer={(canDelete) => (
              <Tooltip
                disabled={canDelete && derivedVariantCount === 0}
                label={
                  !canDelete
                    ? 'You do not have permission to delete this variant. Please contact your graph admin.'
                    : derivedVariantCount
                      ? `This variant has ${derivedVariantCount} derived contract variants. All contract variants must be deleted before deleting the source variant.`
                      : ''
                }
              >
                <Button
                  variant="destructive"
                  isDisabled={
                    !canDelete ||
                    derivedVariantCount > 0 ||
                    !graphType ||
                    !currentAccountId
                  }
                  onClick={() => {
                    history.push(
                      setOverlayVisibleLocation({
                        location,
                        overlayName: Config.modals.deleteGraphVariant,
                      }),
                    );
                  }}
                >
                  Delete variant
                </Button>
              </Tooltip>
            )}
          />
        </CardHeader>
      </Card>

      <URLModal name={Config.modals.deleteGraphVariant}>
        {({ isOpen, closeModal }) => (
          <Modal
            isOpen={isOpen}
            onClose={() => {
              closeModal();
            }}
            size="lg"
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                <ModalTitle>
                  Are you sure you want to delete{' '}
                  {(
                    graphVariant +
                    (graphVariant === 'current' ? ' (default)' : '')
                  ).toUpperCase()}
                  ?
                </ModalTitle>
                <ModalDescription>
                  Deleting your variant will also remove all your related
                  configurations including schema update notification(s).
                </ModalDescription>
              </ModalHeader>
              <ModalBody>
                <p>
                  Deleting this variant will remove it from the {graph?.title}{' '}
                  graph. <em>This action cannot be undone.</em>
                </p>
                {error && <ErrorMessage />}
              </ModalBody>
              <ModalFooter>
                <ButtonGroup>
                  <Button variant="secondary" onClick={() => closeModal()}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    isDisabled={loading}
                    onClick={() => deleteTag()}
                  >
                    Yes, Delete
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}
      </URLModal>
    </>
  );
};
